/**
 * Hook para gerenciar preferências do módulo de Tickets
 * Persiste no localStorage
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { CardDensity, KanbanSortMode } from '@troia-v3/types';
import { TICKET_KANBAN_SORT_MODES } from '@troia-v3/types';
import type { TicketsAdvancedFilters } from '../types';
import { TICKET_CARD_DENSITIES } from '../types';

const STORAGE_KEY = 'tickets_preferences';
const FILTERS_KEY = 'tickets_advanced_filters';

interface TicketsPreferences {
  selectedPipelineId: string | null;
  isSidebarOpen: boolean;
  viewMode: 'kanban' | 'table';
  sortMode: KanbanSortMode;
  cardDensity: CardDensity;
}

const DEFAULT_PREFERENCES: TicketsPreferences = {
  selectedPipelineId: null,
  isSidebarOpen: true,
  viewMode: 'kanban',
  sortMode: 'created',
  // Padrão do Helpdesk: card de Detalhes (`01 Ficha do cliente`). As três
  // densidades existem desde a tarefa C11 — 'detailed' é preferência do módulo,
  // não a única opção disponível.
  cardDensity: 'detailed',
};

/** Lista de sort modes de tickets (`readonly [...]`) tipada como array simples para o `.includes()` de validação. */
const VALID_SORT_MODES = TICKET_KANBAN_SORT_MODES as readonly string[];

/**
 * Whitelist do tamanho de card OFERECIDO no Helpdesk (`TICKET_CARD_DENSITIES`),
 * não a `isCardDensity` genérica do pacote de tipos. Hoje as duas listas
 * coincidem (as três densidades), mas a indireção fica: se o módulo voltar a
 * restringir posições, é esta const que manda, e um valor fora dela cai no
 * default `'detailed'` em vez de deixar uma pill inerte no seletor.
 */
const VALID_CARD_DENSITIES = TICKET_CARD_DENSITIES as readonly string[];

/**
 * Hook para gerenciar preferências do módulo de Tickets
 */
export function useTicketsPreferences() {
  const [preferences, setPreferences] = useState<TicketsPreferences>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Migrate old selectedTeamId to selectedPipelineId
        if (parsed.selectedTeamId !== undefined && parsed.selectedPipelineId === undefined) {
          parsed.selectedPipelineId = null;
          delete parsed.selectedTeamId;
        }
        // Whitelist sortMode contra TICKET_KANBAN_SORT_MODES — valor legado/inválido
        // (ex.: de outro módulo, ou 'idle' que tickets não suporta) cai pro default.
        const sortMode = VALID_SORT_MODES.includes(parsed.sortMode)
          ? (parsed.sortMode as KanbanSortMode)
          : DEFAULT_PREFERENCES.sortMode;
        // Mesma whitelist pro tamanho do card: o espalhamento de `parsed` abaixo
        // deixaria passar qualquer string do blob direto pro board. Contra
        // TICKET_CARD_DENSITIES (as oferecidas no Helpdesk), não contra
        // isCardDensity (o pacote de tipos) — ver comentário acima.
        const cardDensity = VALID_CARD_DENSITIES.includes(parsed.cardDensity)
          ? (parsed.cardDensity as CardDensity)
          : DEFAULT_PREFERENCES.cardDensity;
        return { ...DEFAULT_PREFERENCES, ...parsed, sortMode, cardDensity };
      }
    } catch (e) {
      console.error('Error loading tickets preferences:', e);
    }
    return DEFAULT_PREFERENCES;
  });

  const [advancedFilters, setAdvancedFiltersState] = useState<TicketsAdvancedFilters>(() => {
    try {
      const stored = localStorage.getItem(FILTERS_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error loading tickets filters:', e);
    }
    return {};
  });

  // Skip first save (on mount)
  const isFirstRender = useRef(true);

  // Persist preferences to localStorage
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (e) {
      console.error('Error saving tickets preferences:', e);
    }
  }, [preferences]);

  // Persist filters to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(advancedFilters));
    } catch (e) {
      console.error('Error saving tickets filters:', e);
    }
  }, [advancedFilters]);

  // Setters
  const setSelectedPipelineId = useCallback((pipelineId: string | null) => {
    setPreferences(prev => ({ ...prev, selectedPipelineId: pipelineId }));
  }, []);

  const setIsSidebarOpen = useCallback((isOpen: boolean) => {
    setPreferences(prev => ({ ...prev, isSidebarOpen: isOpen }));
  }, []);

  const setViewMode = useCallback((mode: 'kanban' | 'table') => {
    setPreferences(prev => ({ ...prev, viewMode: mode }));
  }, []);

  const setSortMode = useCallback((mode: KanbanSortMode) => {
    setPreferences(prev => ({ ...prev, sortMode: mode }));
  }, []);

  const setCardDensity = useCallback((density: CardDensity) => {
    setPreferences(prev => ({ ...prev, cardDensity: density }));
  }, []);

  const setAdvancedFilters = useCallback((filters: TicketsAdvancedFilters) => {
    setAdvancedFiltersState(filters);
  }, []);

  return {
    // State
    selectedPipelineId: preferences.selectedPipelineId,
    isSidebarOpen: preferences.isSidebarOpen,
    viewMode: preferences.viewMode,
    sortMode: preferences.sortMode,
    cardDensity: preferences.cardDensity,
    advancedFilters,

    // Setters
    setSelectedPipelineId,
    setIsSidebarOpen,
    setViewMode,
    setSortMode,
    setCardDensity,
    setAdvancedFilters,
  };
}
